import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

/* ─── Types ─── */

interface TicketInfo {
  registrationId: string;
  tierName: string;
  priceAtPurchase: number;
  status: 'Active' | 'Cancelled';
  referenceId: string;
  orderReferenceId?: string | null;
  checkedIn?: boolean;
}

interface EventInfo {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venueName: string;
  venueAddress?: string;
  rsvpStatus?: string | null;
  mealInfo?: string | null;
  parkingInfo?: string | null;
  transportInfo?: string | null;
}

interface MealInfo {
  mealOptionName: string;
  mealOptionIcon: string | null;
  mealOptionColor: string | null;
}

interface SeatInfo {
  tableNumber: number;
  seatNumber: number;
  tableName?: string;
  zoneName?: string;
}

interface AnnouncementInfo {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

interface NowEventInfo {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  venueName: string;
}

interface MyWeekendResponse {
  ticket: TicketInfo | null;
  isCheckedIn?: boolean;
  events: EventInfo[];
  meal: MealInfo | null;
  seat: SeatInfo | null;
  announcements: AnnouncementInfo[];
  purchases: Array<{ id: string; title: string; category: string; amount: number | null }>;
  currentEvent: NowEventInfo | null;
  nextEvent: NowEventInfo | null;
}

interface FullEvent {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venue?: {
    name: string;
    address?: string;
    parkingInfo?: string;
    parkingImageUrl?: string;
  };
  transportInfo?: string;
}

interface InboxAnnouncement {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

interface WelcomePackageInfo {
  enabled: boolean;
  title: string;
  body: string;
  location: string | null;
  hours: string | null;
}

/* ─── Utilities ─── */

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function getTodayDateString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getGoogleMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/* ─── Styles ─── */

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1rem',
    maxWidth: '600px',
    margin: '0 auto',
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1.25rem',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 0.25rem 0',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    padding: '1.25rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
    marginBottom: '0.2rem',
  },
  value: {
    fontSize: '0.9rem',
    color: TEXT_DARK,
    marginBottom: '0.5rem',
  },
  badge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  directionsBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#e8f5e9',
    color: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    marginTop: '0.4rem',
  },
  eventItem: {
    padding: '0.75rem 0',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
  },
  loading: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '1rem',
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem',
    backgroundColor: '#fdecea',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    color: '#d32f2f',
  },
  retryBtn: {
    padding: '0.6rem 1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.75rem',
  },
};

/* ─── Dress Code Data ─── */

const DRESS_CODES = [
  { day: 'Friday', code: 'Smart Casual', emoji: '👔', note: 'Relaxed but polished — daytime events and welcome reception' },
  { day: 'Saturday (Legacy Ball)', code: 'Formal / Black-Tie', emoji: '🎩', note: 'Gala attire — evening gowns, tuxedos, traditional formal wear' },
  { day: 'Sunday', code: 'Smart Casual', emoji: '👗', note: 'Comfortable and stylish — farewell brunch and departure' },
];

/* ─── Component ─── */

export function GuestToolkitPage() {
  const { user } = useAuth();
  const [weekendData, setWeekendData] = useState<MyWeekendResponse | null>(null);
  const [allEvents, setAllEvents] = useState<FullEvent[]>([]);
  const [announcements, setAnnouncements] = useState<InboxAnnouncement[]>([]);
  const [welcomePackage, setWelcomePackage] = useState<WelcomePackageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [weekend, events, inbox, wpkg] = await Promise.all([
        apiClient<MyWeekendResponse>('/my-weekend'),
        apiClient<FullEvent[]>('/events'),
        apiClient<InboxAnnouncement[]>('/announcements/inbox').catch(() => []),
        apiClient<WelcomePackageInfo>('/my-weekend/welcome-package').catch(() => null),
      ]);
      setWeekendData(weekend);
      setAllEvents(Array.isArray(events) ? events : []);
      setAnnouncements(Array.isArray(inbox) ? inbox.slice(0, 3) : []);
      setWelcomePackage(wpkg);
    } catch {
      setError('Unable to load your guest toolkit. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Loading your guest toolkit...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.page}>
        <div style={styles.error}>
          <p>{error}</p>
          <button style={styles.retryBtn} onClick={fetchData}>Retry</button>
        </div>
      </div>
    );
  }

  const today = getTodayDateString();
  const todaysEvents = weekendData?.events.filter((e) => {
    const eventDate = e.date?.includes('T') ? e.date.split('T')[0] : e.date;
    return eventDate === today;
  }).sort((a, b) => a.startTime.localeCompare(b.startTime)) || [];

  // Gather unique venues from all events
  const venuesMap = new Map<string, { name: string; address?: string; parkingInfo?: string; parkingImageUrl?: string }>();
  allEvents.forEach((evt) => {
    if (evt.venue && evt.venue.name && !venuesMap.has(evt.venue.name)) {
      venuesMap.set(evt.venue.name, evt.venue);
    }
  });
  // Also from weekend data
  weekendData?.events.forEach((evt) => {
    if (evt.venueName && !venuesMap.has(evt.venueName)) {
      venuesMap.set(evt.venueName, { name: evt.venueName, address: evt.venueAddress });
    }
  });
  const venues = Array.from(venuesMap.values());

  // Parking & transport info from events
  const parkingEntries = allEvents
    .filter((e) => e.venue?.parkingInfo || e.transportInfo)
    .map((e) => ({
      eventName: e.name,
      venueName: e.venue?.name || '',
      parkingInfo: e.venue?.parkingInfo || null,
      transportInfo: e.transportInfo || null,
    }));
  // Also from weekend data
  weekendData?.events.forEach((e) => {
    if ((e.parkingInfo || e.transportInfo) && !parkingEntries.find((p) => p.eventName === e.name)) {
      parkingEntries.push({
        eventName: e.name,
        venueName: e.venueName,
        parkingInfo: e.parkingInfo || null,
        transportInfo: e.transportInfo || null,
      });
    }
  });

  return (
    <div style={styles.page}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>🧳 Guest Toolkit</h1>
        <p style={styles.subtitle}>
          Your digital dossier for the weekend{user?.fullName ? ` — ${user.fullName}` : ''}
        </p>
      </header>

      {/* 0. Welcome Package (admin-managed) */}
      {welcomePackage && welcomePackage.enabled && (
        <section style={{ ...styles.card, backgroundColor: '#fef9e7', border: '1px solid #f5e6a3' }} aria-label="Welcome Package">
          <h2 style={{ ...styles.cardTitle, color: '#92400e' }}>🎁 {welcomePackage.title}</h2>
          <p style={{ fontSize: '0.88rem', color: TEXT_DARK, margin: '0 0 0.5rem 0', lineHeight: 1.5 }}>
            {welcomePackage.body}
          </p>
          {welcomePackage.location && (
            <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginBottom: '0.3rem' }}>
              📍 <strong>Location:</strong> {welcomePackage.location}
            </div>
          )}
          {welcomePackage.hours && (
            <div style={{ fontSize: '0.82rem', color: TEXT_DARK }}>
              🕐 <strong>Hours:</strong> {welcomePackage.hours}
            </div>
          )}
        </section>
      )}

      {/* 1. My Ticket */}
      <section style={styles.card} aria-label="My Ticket">
        <h2 style={styles.cardTitle}>🎫 My Ticket</h2>
        {weekendData?.ticket ? (
          <div>
            {weekendData.ticket.orderReferenceId && (
              <>
                <div style={styles.label}>ORDER / REFERENCE</div>
                <div style={{ ...styles.value, fontWeight: 700 }}>{weekendData.ticket.orderReferenceId}</div>
              </>
            )}
            <div style={styles.label}>Ticket Number</div>
            <div style={styles.value}>{weekendData.ticket.referenceId}</div>
            <div style={styles.label}>Tier</div>
            <div style={styles.value}>{weekendData.ticket.tierName}</div>
            <div style={styles.label}>Status</div>
            <div>
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: weekendData.ticket.status === 'Active' ? '#dcfce7' : '#fef3c7',
                  color: weekendData.ticket.status === 'Active' ? '#166534' : '#92400e',
                }}
              >
                {weekendData.ticket.status}
              </span>
              {weekendData.isCheckedIn && (
                <span
                  style={{ ...styles.badge, backgroundColor: '#ede7f6', color: '#6a1b9a', marginLeft: '0.5rem' }}
                >
                  ✓ Checked In
                </span>
              )}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No ticket information available.</p>
        )}
      </section>

      {/* 2. What's Next */}
      <section style={styles.card} aria-label="What's Next">
        <h2 style={styles.cardTitle}>📅 What&apos;s Next</h2>
        {weekendData?.nextEvent ? (
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: TEXT_DARK }}>
              {weekendData.nextEvent.name}
            </div>
            <div style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginTop: '0.3rem' }}>
              {formatTime(weekendData.nextEvent.startTime)} – {formatTime(weekendData.nextEvent.endTime)}
            </div>
            <div style={{ fontSize: '0.85rem', color: TEXT_DARK, marginTop: '0.2rem' }}>
              📍 {weekendData.nextEvent.venueName}
            </div>
            {/* Countdown */}
            {(() => {
              const start = new Date(weekendData.nextEvent.startTime);
              const diffMs = start.getTime() - Date.now();
              if (diffMs > 0) {
                const totalMinutes = Math.floor(diffMs / 60000);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                const text = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                return (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: '#b45309',
                      backgroundColor: 'rgba(251,191,36,0.15)',
                      display: 'inline-block',
                      padding: '0.25rem 0.6rem',
                      borderRadius: '4px',
                    }}
                  >
                    ⏱ Starts in {text}
                  </div>
                );
              }
              return null;
            })()}
            {/* Get Directions */}
            {(() => {
              const match = weekendData.events.find((e) => e.id === weekendData.nextEvent!.id);
              const addr = match?.venueAddress;
              if (addr) {
                return (
                  <a
                    href={getGoogleMapsUrl(addr)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.directionsBtn}
                    aria-label={`Get directions to ${weekendData.nextEvent.venueName}`}
                  >
                    📍 Get Directions
                  </a>
                );
              }
              return null;
            })()}
          </div>
        ) : weekendData?.currentEvent ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.3rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#dc2626' }}>🔴</span>
              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#15803d' }}>Happening Now</span>
            </div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: TEXT_DARK }}>
              {weekendData.currentEvent.name}
            </div>
            <div style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginTop: '0.2rem' }}>
              {formatTime(weekendData.currentEvent.startTime)} – {formatTime(weekendData.currentEvent.endTime)} · {weekendData.currentEvent.venueName}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No upcoming events at this time.</p>
        )}
      </section>

      {/* 3. Today's Events */}
      <section style={styles.card} aria-label="Today's Events">
        <h2 style={styles.cardTitle}>🗓️ Today&apos;s Events</h2>
        {todaysEvents.length > 0 ? (
          <div>
            {todaysEvents.map((evt, idx) => (
              <div
                key={evt.id}
                style={{
                  ...styles.eventItem,
                  borderBottom: idx === todaysEvents.length - 1 ? 'none' : styles.eventItem.borderBottom,
                }}
              >
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>{evt.name}</div>
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.15rem' }}>
                  {formatTime(evt.startTime)} – {formatTime(evt.endTime)} · {evt.venueName}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No events scheduled for today.</p>
        )}
      </section>

      {/* 4. My Meal & Seat */}
      <section style={styles.card} aria-label="Meal and Seat">
        <h2 style={styles.cardTitle}>🍽️ My Meal &amp; Seat</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <div style={styles.label}>Meal Selection</div>
            {weekendData?.meal ? (
              <div style={styles.value}>
                {weekendData.meal.mealOptionIcon && <span>{weekendData.meal.mealOptionIcon} </span>}
                {weekendData.meal.mealOptionName}
              </div>
            ) : (
              <div style={{ ...styles.value, color: TEXT_MUTED }}>Not selected</div>
            )}
          </div>
          <div>
            <div style={styles.label}>Table / Seat</div>
            {weekendData?.seat ? (
              <div style={styles.value}>
                Table {weekendData.seat.tableName || weekendData.seat.tableNumber} · Seat {weekendData.seat.seatNumber}
                {weekendData.seat.zoneName && (
                  <div style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>{weekendData.seat.zoneName} Zone</div>
                )}
              </div>
            ) : (
              <div style={{ ...styles.value, color: TEXT_MUTED }}>Not assigned</div>
            )}
          </div>
        </div>
      </section>

      {/* 5. Venues & Logistics (grouped by venue) */}
      <section style={styles.card} aria-label="Venues and Logistics">
        <h2 style={styles.cardTitle}>📍 Venues &amp; Logistics</h2>
        {venues.length > 0 ? (
          <div>
            {venues.map((v, idx) => {
              const relatedParking = parkingEntries.filter(
                (p) => p.venueName === v.name
              );
              return (
                <div
                  key={v.name}
                  style={{
                    ...styles.eventItem,
                    borderBottom: idx === venues.length - 1 ? 'none' : styles.eventItem.borderBottom,
                  }}
                >
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>{v.name}</div>
                  {v.address && (
                    <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.15rem' }}>{v.address}</div>
                  )}
                  {/* Events at this venue */}
                  {(() => {
                    const venueEvents = allEvents.filter(
                      (e) => e.venue?.name === v.name
                    );
                    if (venueEvents.length === 0) return null;
                    return (
                      <ul style={{ margin: '0.4rem 0 0.3rem 1.2rem', padding: 0, listStyleType: 'disc' }}>
                        {venueEvents.map((evt) => (
                          <li key={evt.id} style={{ fontSize: '0.8rem', color: TEXT_DARK, marginBottom: '0.2rem' }}>
                            {evt.name}
                            {evt.date && <span style={{ color: TEXT_MUTED }}> — {formatDate(evt.date)}</span>}
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                  {v.parkingInfo && (
                    <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginTop: '0.3rem' }}>
                      🅿️ {v.parkingInfo}
                    </div>
                  )}
                  {v.parkingImageUrl && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <img src={v.parkingImageUrl} alt="Parking map" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid #e0e0e0' }} />
                    </div>
                  )}
                  {relatedParking.map((p, pi) => (
                    <div key={pi}>
                      {p.transportInfo && (
                        <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginTop: '0.2rem' }}>
                          🚐 {p.transportInfo}
                        </div>
                      )}
                      {p.parkingInfo && !v.parkingInfo && (
                        <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginTop: '0.2rem' }}>
                          🅿️ {p.parkingInfo}
                        </div>
                      )}
                    </div>
                  ))}
                  {v.address && (
                    <a
                      href={getGoogleMapsUrl(v.address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.directionsBtn}
                      aria-label={`Get directions to ${v.name}`}
                    >
                      📍 Get Directions
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No venue information available.</p>
        )}
      </section>

      {/* 6. Dress Code */}
      <section style={styles.card} aria-label="Dress Code">
        <h2 style={styles.cardTitle}>👗 Dress Code</h2>
        <div>
          {DRESS_CODES.map((dc) => (
            <div key={dc.day} style={{ ...styles.eventItem, borderBottom: `1px solid ${BORDER_LIGHT}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span>{dc.emoji}</span>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>{dc.day}</span>
              </div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: PRIMARY, marginTop: '0.15rem' }}>
                {dc.code}
              </div>
              <div style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginTop: '0.1rem' }}>{dc.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 6.4 Vendor Showcase */}
      <section style={{ ...styles.card, backgroundColor: '#fef9f0', border: '1px solid #fde68a' }} aria-label="Vendor Showcase">
        <h2 style={{ ...styles.cardTitle, color: '#92400e' }}>🏪 Vendor Showcase</h2>
        <p style={{ fontSize: '0.85rem', color: TEXT_DARK, margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          Preview food, merchandise, and services from reunion weekend vendors before you arrive.
        </p>
        <Link
          to="/vendor-showcase"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#92400e',
            borderRadius: '10px',
            textDecoration: 'none',
            minHeight: '48px',
            width: '100%',
            boxSizing: 'border-box' as const,
            WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
          }}
          aria-label="Browse vendor showcase"
        >
          🏪 Browse Vendors
        </Link>
        <Link
          to="/food-catalog"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px 20px',
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#e65100',
            borderRadius: '10px',
            textDecoration: 'none',
            minHeight: '48px',
            width: '100%',
            boxSizing: 'border-box' as const,
            WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
            marginTop: '0.5rem',
          }}
          aria-label="Browse food catalog"
        >
          🍽️ Food Catalog
        </Link>
      </section>

      {/* 6.5 Nearby Places */}
      <section style={{ ...styles.card, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' }} aria-label="Nearby Places">
        <h2 style={{ ...styles.cardTitle, color: '#166534' }}>📍 Nearby Places</h2>
        <p style={{ fontSize: '0.85rem', color: TEXT_DARK, margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          Find restaurants, pharmacies, gas stations, ATMs and more near event venues.
        </p>
        <Link
          to="/nearby"
          style={{
            display: 'inline-block',
            padding: '10px 16px',
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#1a6b3c',
            borderRadius: '8px',
            textDecoration: 'none',
            minHeight: '44px',
            lineHeight: '24px',
          }}
          aria-label="Find nearby places"
        >
          Explore Nearby Places
        </Link>
      </section>

      {/* 7. Emergency & Help */}
      <section style={styles.card} aria-label="Emergency and Help">
        <h2 style={styles.cardTitle}>🆘 Emergency &amp; Help</h2>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#dc2626' }}>Emergency Services</div>
          <div style={{ fontSize: '0.85rem', color: TEXT_DARK, marginTop: '0.15rem' }}>
            Call <a href="tel:911" style={{ color: '#dc2626', fontWeight: 700 }}>911</a> for medical emergencies, fire, or police.
          </div>
        </div>
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>Event Organizer</div>
          <div style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginTop: '0.15rem' }}>
            Ask any committee member wearing a host badge for assistance.
          </div>
        </div>
      </section>

      {/* 9. Latest News */}
      <section style={styles.card} aria-label="Latest News">
        <h2 style={styles.cardTitle}>📢 Latest News</h2>
        {announcements.length > 0 ? (
          <div>
            {announcements.map((a, idx) => (
              <div
                key={a.id}
                style={{
                  ...styles.eventItem,
                  borderBottom: idx === announcements.length - 1 ? 'none' : styles.eventItem.borderBottom,
                }}
              >
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: TEXT_DARK }}>{a.title}</div>
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.15rem' }}>
                  {a.body?.length > 120 ? a.body.slice(0, 120) + '…' : a.body}
                </div>
                <div style={{ fontSize: '0.7rem', color: TEXT_MUTED, marginTop: '0.2rem' }}>
                  {formatDate(a.createdAt)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No announcements yet.</p>
        )}
      </section>
    </div>
  );
}
